import React, { useEffect } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
} from 'antd';
import {
  CREATE_ERROR,
  EDIT_ERROR,
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { PRPService } from 'services/PRPService';
import { requiredRule } from 'utils/forms/validationRules';
import { thousandFormatter } from 'utils/forms/formatter';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';

function PRPForm({ isActive, onClose, onSuccess, initialValues }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const {
        code,
        name,
        work_rate,
        ip_rate,
        base,
        max_value,
        max_tax_income,
        tax_deducation,
        tax_without_deducation,
        begin_date,
        end_date,
      } = initialValues;

      form.setFieldsValue({
        code,
        name,
        work_rate,
        ip_rate,
        base,
        max_value,
        max_tax_income,
        tax_deducation,
        tax_without_deducation,
        begin_date: dayjs(begin_date),
        end_date: dayjs(end_date),
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const compareEndDate = () => {
    form.setFieldsValue({ end_date: null });
  };

  const compareBeginDate = (currentDate) =>
    currentDate && currentDate < form.getFieldValue('begin_date');

  //   TODO: make date by this format YYYY-MM-DD backend help
  const handleSubmit = (values) => {
    const payload = {
      ...values,
      id: initialValues?.id,
      begin_date: values.begin_date.format('YYYY-MM-DD'),
      end_date: values.end_date.format('YYYY-MM-DD'),
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await PRPService.createPRP(data);

      if (isCreated) {
        handleConfirm();
        message.success(SUCCESS_CREATE);
      }
    } catch (err) {
      message.error(CREATE_ERROR);
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, id) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await PRPService.editPRP(data, id);

      if (isUpdated) {
        handleConfirm();
        message.success(SUCCESS_UPDATE);
      }
    } catch (err) {
      message.error(EDIT_ERROR);
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onClose();
    onSuccess();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование ПРП' : 'Создание ПРП'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item name="code" label="Код" rules={[requiredRule]}>
          <Input placeholder="Введите Код" allowClear />
        </Form.Item>

        <Form.Item name="name" label="Статья" rules={[requiredRule]}>
          <Input placeholder="Введите статью" allowClear />
        </Form.Item>

        <Form.Item name="work_rate" label="Ставка для работников">
          <Input placeholder="Введите ставку для работника" allowClear />
        </Form.Item>

        <Form.Item name="ip_rate" label="Ставка для ИП">
          <Input placeholder="Введите ставку для работника" allowClear />
        </Form.Item>

        <Form.Item name="base" label="База" rules={[requiredRule]}>
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="База"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="max_value" label="Макс">
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Макс"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="max_tax_income"
          label="Предельный доход налогооблажения"
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Предельный доход налогооблажения"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="tax_deducation"
          label="Объект налогоблажение с вычетом"
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Объект налогоблажение с вычетом"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item
          name="tax_without_deducation"
          label="Объект налогоблажение без вычетом"
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Объект налогоблажение без вычетом"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="begin_date" label="Дата начала" rules={[requiredRule]}>
          <DatePicker
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
            onChange={compareEndDate}
          />
        </Form.Item>

        <Form.Item
          name="end_date"
          label="Дата окончание"
          rules={[requiredRule]}
        >
          <DatePicker
            disabledDate={compareBeginDate}
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { PRPForm };
